/**
 * WHOLE ROWS IN A FLUID GRID.
 *
 * The listing grids are `repeat(auto-fill, minmax(…))`, so the column count is
 * whatever the width allows — 2 on a phone, 5 on a laptop, 7 on a wide screen —
 * and a fixed batch (12, 15, 24) left a part-empty last row at most widths. No
 * single batch divides 2…7 and fixing the column counts would bend the cards,
 * so the page reads its own column count and sizes what it shows to it.
 */

/** Columns in a computed `grid-template-columns` ("236px 236px 236px" → 3). */
export function countColumns(template: string): number {
  if (!template || template === "none") return 0;
  return template.replace(/\[[^\]]*\]/g, " ").trim().split(/\s+/).filter(Boolean).length;
}

/**
 * How many of the loaded items to show so the last row is full: the wanted
 * count rounded up when enough are loaded, otherwise down (the rest wait for
 * the next batch, they are not lost). Only the end of the whole list — nothing
 * hidden, nothing more to fetch — may leave a part row.
 */
export function fullRows(want: number, loaded: number, columns: number, ended: boolean): number {
  const n = Math.min(want, loaded);
  if (columns <= 1 || n === 0) return n;
  if (ended && n === loaded) return n;
  const up = Math.ceil(n / columns) * columns;
  if (up <= loaded) return up;
  const down = Math.floor(n / columns) * columns;
  return down > 0 ? down : n;
}

/**
 * A numbered page's size that fills whole rows: the multiple of the column
 * count nearest to the usual size (15 → 15 at 5 columns, 16 at 4, 18 at 6).
 * Numbered pages cannot hide a remainder — page 2 starts after it — so the
 * page itself is cut to the grid. Unknown columns keep the usual size.
 */
export function pageSizeFor(columns: number, base: number): number {
  if (columns <= 1) return base;
  return Math.max(columns, Math.round(base / columns) * columns);
}
